import type { SubmitRequestUnion } from "@nlc/shared-types";
import type { UseFormReturn } from "react-hook-form";

import {
  BriefCreatorTypeEnum,
  isCustomerRole,
  RequestTypeEnum,
  submitRequestUnionZod,
} from "@nlc/shared-types";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { toast } from "@/components/toast/toast-queue";
import { useIsMBCSubmitted } from "@/features/request/create/shared/useIsMBCSubmitted";
import { countNonEmptyFields } from "@/hooks/posthog/countNonEmptyFields";
import {
  formTypeMap,
  useCapturePosthogEvent,
} from "@/hooks/posthog/useCapturePosthogEvent";
import { useIsRequestFormDirty } from "@/hooks/useIsRequestFormDirty";
import { useUser } from "@/hooks/useUser";
import authenticatedApi from "@/lib/api/client";

import type { CreateGraphicRequestFormData } from "../components/useCreateGraphicRequestForm";

import { useNavigateToRequestList } from "../../shared/useNavigateToRequestList";
import { getValidAttachmentIds } from "../../shared/utils";

type UseSubmitMutationProps = {
  draftId: number | undefined;
  formMethods: UseFormReturn<CreateGraphicRequestFormData>;
};

export function useSubmitMutation({
  draftId,
  formMethods,
}: UseSubmitMutationProps) {
  const { data: user } = useUser();
  const resetFormDirty = useIsRequestFormDirty((state) => state.reset);
  const setIsMBCSubmitted = useIsMBCSubmitted((state) => state.setIsSubmitted);
  const capture = useCapturePosthogEvent();
  const navigateToRequestList = useNavigateToRequestList();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (formData: CreateGraphicRequestFormData) => {
      if (!draftId) throw new Error("No request ID found");

      const commonFields: SubmitRequestUnion = {
        attachmentIds: [
          ...getValidAttachmentIds(formData.assets),
          ...getValidAttachmentIds(formData.inspirations),
        ],
        brandId: formData.brand ?? undefined,
        briefCreatorType: formData.briefCreatorType,
        collaboratorIds: formData.collaboratorIds,
        complexityId: formData.designComplexity,
        creativeDirection: formData.creativeDirection,
        designTypeId: formData.designType,
        name: formData.requestName,
        preferredDesignerIds: formData.preferredDesignerIds,
        preferredFileType: formData.fileTypes,
        requestType: RequestTypeEnum.GRAPHIC,
        restrictAiAssets: formData.restrictAiAssets,
        sizesNeeded: formData.sizes,
      };

      const mbcFields = formData.briefCreatorDetailed;

      const briefTypeFields =
        formData.briefCreatorType === BriefCreatorTypeEnum.DETAILED
          ? {
              briefText: mbcFields.finalBriefHTML,
              description: formData.briefCreatorSingle.requestDescriptionHTML,
              textForDesign: mbcFields.keyMessageAndCta,
            }
          : {
              briefText: undefined,
              description: formData.briefCreatorSingle.requestDescriptionHTML,
              textForDesign: formData.briefCreatorSingle.textsForDesignHTML,
            };

      const magicalBriefCreatorFields =
        formData.briefCreatorType === BriefCreatorTypeEnum.DETAILED
          ? {
              magicalBriefCreator: {
                additionalGuidelines: mbcFields.additionalGuidelines,
                brandGuidelines: mbcFields.brandGuidelines,
                creativeGoals: mbcFields.creativeGoal,
                keyMessageAndCta: mbcFields.keyMessageAndCta,
                scope: mbcFields.scope,
              },
            }
          : {};

      const parsed = submitRequestUnionZod.parse({
        ...commonFields,
        ...briefTypeFields,
        ...magicalBriefCreatorFields,
      });

      return authenticatedApi
        .url(`/requests/${draftId}/submit`)
        .post(parsed)
        .json<{ message: string; redirectTo?: string; requestId?: number }>();
    },
    onError: (error: { message: string; statusCode: number }) => {
      console.error("Failed to submit request:", error);

      const { briefCreatorType: _briefCreatorType, ...formValues } =
        formMethods.getValues();

      capture("submit_request", {
        ai_usage_flag:
          formMethods.getValues("briefCreatorType") ===
          BriefCreatorTypeEnum.DETAILED,
        brief_type: "graphic",
        form_version: formTypeMap[formMethods.getValues("briefCreatorType")],
        input_fields_completed: countNonEmptyFields(formValues),
        submission_success: false,
      });

      if (error.message.includes("Request name already exists")) {
        formMethods.setError("requestName", {
          message: "Request name already exists",
        });
        formMethods.setFocus("requestName");
      }

      if (error.statusCode === 400) {
        const errorMessage = error.message;

        if (
          [
            "No credits available",
            "Subscription ended",
            "Subscription is cancelled",
          ].includes(errorMessage)
        ) {
          navigateToRequestList();

          return;
        }
      }

      // Handle kickstarter account trying to submit
      if (error.statusCode === 403) {
        toast.add(
          {
            description: error.message,
            title: "Cannot submit request",
            type: "error",
          },
          {
            timeout: 8000,
          },
        );

        return;
      }

      toast.add(
        {
          description: error.message,
          title: "Error submitting request",
          type: "error",
        },
        {
          timeout: 5000,
        },
      );
    },
    onMutate: () => {
      formMethods.reset(formMethods.getValues());
      resetFormDirty();
    },
    onSuccess: (response) => {
      const { briefCreatorType: _briefCreatorType, ...formValues } =
        formMethods.getValues();

      capture("submit_request", {
        ai_usage_flag:
          formMethods.getValues("briefCreatorType") ===
          BriefCreatorTypeEnum.DETAILED,
        brief_type: "graphic",
        form_version: formTypeMap[formMethods.getValues("briefCreatorType")],
        input_fields_completed: countNonEmptyFields(formValues),
        submission_success: true,
      });

      setIsMBCSubmitted(false); // reset the MBC submission flag

      queryClient.invalidateQueries({ queryKey: ["user"] });

      // Dispatch custom event to trigger statistics update
      window.dispatchEvent(
        new CustomEvent("requestCreated", {
          detail: { requestId: response.requestId },
        }),
      );

      // Check if user has customer role (owner, admin, collaborator)
      const isCustomer = user && isCustomerRole(user.user.roleId);

      navigateToRequestList({ showProcessing: !!isCustomer });
    },
  });
}
